/**
 * Website Message module chrome (Settings → Message).
 * Lives in storefront.moduleSettings.siteMessage (exported with storefront JSON).
 */

import type { Language } from '../types';
import { _t, pickI18nText } from './i18n';
import {
  normalizeNoteColorRole,
  type NoteColorRole,
} from './noteBlog';
import {
  normalizeAppearanceFontWeight,
  normalizeAppearancePercentScale,
  STOREFRONT_MENU_FONT_SCALE_MAX,
  STOREFRONT_MENU_FONT_SCALE_MIN,
  type StorefrontTitleI18n,
} from './storefrontAppearance';

export type StorefrontSiteMessageSettings = {
  /** Padding on all sides (px) for `#website-message-zone`. */
  insetPx: number;
  bgRole: NoteColorRole;
  textRole: NoteColorRole;
  fieldBgRole: NoteColorRole;
  fieldFgRole: NoteColorRole;
  fieldBorderRole: NoteColorRole;
  /** Empty lang → fall back to locale `_t` keys. */
  titleI18n: StorefrontTitleI18n;
  nameLabelI18n: StorefrontTitleI18n;
  emailLabelI18n: StorefrontTitleI18n;
  phoneLabelI18n: StorefrontTitleI18n;
  messageLabelI18n: StorefrontTitleI18n;
  messagePlaceholderI18n: StorefrontTitleI18n;
  submitLabelI18n: StorefrontTitleI18n;
  successI18n: StorefrontTitleI18n;
  /** Title size as % of 1.25rem (100–300). */
  titleFontScale: number;
  titleFontWeight: number;
  /** Field labels as % of 0.875rem (50–200). */
  labelFontScale: number;
  labelFontWeight: number;
  /** Inputs / textarea as % of 1rem (50–200). */
  fieldFontScale: number;
  fieldFontWeight: number;
};

const EMPTY_I18N: StorefrontTitleI18n = { ru: '', en: '', he: '' };

export const DEFAULT_STOREFRONT_SITE_MESSAGE: StorefrontSiteMessageSettings = {
  insetPx: 0,
  bgRole: 'pageBg',
  textRole: 'textPrimary',
  fieldBgRole: 'pageBg',
  fieldFgRole: 'textPrimary',
  fieldBorderRole: 'borderColor',
  titleI18n: { ...EMPTY_I18N },
  nameLabelI18n: { ...EMPTY_I18N },
  emailLabelI18n: { ...EMPTY_I18N },
  phoneLabelI18n: { ...EMPTY_I18N },
  messageLabelI18n: { ...EMPTY_I18N },
  messagePlaceholderI18n: { ...EMPTY_I18N },
  submitLabelI18n: { ...EMPTY_I18N },
  successI18n: { ...EMPTY_I18N },
  titleFontScale: 100,
  titleFontWeight: 600,
  labelFontScale: 100,
  labelFontWeight: 500,
  fieldFontScale: 100,
  fieldFontWeight: 400,
};

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

function clampInt(raw: unknown, min: number, max: number, fallback: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function normalizeTitleI18n(raw: unknown): StorefrontTitleI18n {
  const source = asRecord(raw);
  return {
    ru: String(source.ru ?? '').trim(),
    en: String(source.en ?? '').trim(),
    he: String(source.he ?? '').trim(),
  };
}

export function normalizeStorefrontSiteMessage(raw: unknown): StorefrontSiteMessageSettings {
  const source = asRecord(raw);
  const d = DEFAULT_STOREFRONT_SITE_MESSAGE;
  return {
    insetPx: clampInt(source.insetPx ?? source.inset_px, 0, 64, d.insetPx),
    bgRole: normalizeNoteColorRole(source.bgRole ?? source.bg_role, d.bgRole),
    textRole: normalizeNoteColorRole(source.textRole ?? source.text_role, d.textRole),
    fieldBgRole: normalizeNoteColorRole(
      source.fieldBgRole ?? source.field_bg_role,
      d.fieldBgRole,
    ),
    fieldFgRole: normalizeNoteColorRole(
      source.fieldFgRole ?? source.field_fg_role,
      d.fieldFgRole,
    ),
    fieldBorderRole: normalizeNoteColorRole(
      source.fieldBorderRole ?? source.field_border_role,
      d.fieldBorderRole,
    ),
    titleI18n: normalizeTitleI18n(source.titleI18n ?? source.title_i18n),
    nameLabelI18n: normalizeTitleI18n(source.nameLabelI18n ?? source.name_label_i18n),
    emailLabelI18n: normalizeTitleI18n(source.emailLabelI18n ?? source.email_label_i18n),
    phoneLabelI18n: normalizeTitleI18n(source.phoneLabelI18n ?? source.phone_label_i18n),
    messageLabelI18n: normalizeTitleI18n(
      source.messageLabelI18n ?? source.message_label_i18n,
    ),
    messagePlaceholderI18n: normalizeTitleI18n(
      source.messagePlaceholderI18n ?? source.message_placeholder_i18n,
    ),
    submitLabelI18n: normalizeTitleI18n(
      source.submitLabelI18n ?? source.submit_label_i18n,
    ),
    successI18n: normalizeTitleI18n(source.successI18n ?? source.success_i18n),
    titleFontScale: normalizeAppearancePercentScale(
      source.titleFontScale ?? source.title_font_scale,
      d.titleFontScale,
      STOREFRONT_MENU_FONT_SCALE_MIN,
      300,
    ),
    titleFontWeight: normalizeAppearanceFontWeight(
      source.titleFontWeight ?? source.title_font_weight,
      d.titleFontWeight,
    ),
    labelFontScale: normalizeAppearancePercentScale(
      source.labelFontScale ?? source.label_font_scale,
      d.labelFontScale,
      STOREFRONT_MENU_FONT_SCALE_MIN,
      STOREFRONT_MENU_FONT_SCALE_MAX,
    ),
    labelFontWeight: normalizeAppearanceFontWeight(
      source.labelFontWeight ?? source.label_font_weight,
      d.labelFontWeight,
    ),
    fieldFontScale: normalizeAppearancePercentScale(
      source.fieldFontScale ?? source.field_font_scale,
      d.fieldFontScale,
      STOREFRONT_MENU_FONT_SCALE_MIN,
      STOREFRONT_MENU_FONT_SCALE_MAX,
    ),
    fieldFontWeight: normalizeAppearanceFontWeight(
      source.fieldFontWeight ?? source.field_font_weight,
      d.fieldFontWeight,
    ),
  };
}

/** Custom triple if non-empty, else locale fallback. */
export function resolveSiteMessageText(
  custom: StorefrontTitleI18n,
  fallbackKey: string,
  lang: Language,
): string {
  const picked = pickI18nText(custom, lang);
  return picked || _t(fallbackKey, lang);
}
